<template>
    <mini-calculator-page>
        <div slot="summary-panel">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Earnings Calculator" icon="earnings"/>

                <control label="Date of Birth">
                    <date-input @input="reCalculateRetired" v-model="dateOfBirth"></date-input>
                </control>

                <control label="Gender">
                    <select ref="gender"
                            @change="reCalculateRetired" v-model="gender">
                        <option :value="null">Please select...</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </control>

                <control label="Start of Earnings">
                    <date-input @input="reCalculateEarnings" ref="dateFrom"
                                v-model="dateFrom"></date-input>
                    <control-error :show="!dateFromIsValid()">
                        Date is required, must be in the format DD/MM/YYYY and must be after 06/04/2008.
                    </control-error>
                </control>

                <control label="End of Earnings">
                    <date-input ref="dateTo" @input="reCalculateEarnings" v-model="dateTo"></date-input>
                    <control-error :show="!dateToIsValid()">
                        Date is required, must be in the format DD/MM/YYYY, and must be after both 06/04/2008 and the 'Start of Earnings'.
                    </control-error>
                </control>

                <control label="Employment Type"
                         v-show="!isRetired">
                    <select ref="employmentType"
                            @change="reCalculateEarnings" v-model="employmentType">
                        <option :value="null">Please select...</option>
                        <option value="employed">Employed</option>
                        <option value="self_employed">Self-employed</option>
                    </select>
                </control>

                <control label="Employment Income">
                    <div class="a-form-inputs--half-calcs">
                        <div class="a-form-inputs__container a-form-inputs--pound">
                            <input autocomplete="off" @input="reCalculateEarnings" v-model="employmentIncome"
                                   ref="netOrGrossFirstText"
                                   v-on:focus="employmentIncome = employmentIncome || ''"
                                   v-on:blur="employmentIncome = employmentIncome || 0"
                                   v-two-dp
                                   type="number">
                        </div>
                        <div class="a-form-inputs__container">
                            <select ref="netOrGrossFirst"
                                    @change="reCalculateEarnings"
                                    v-model="employmentIncomeNetGross">
                                <option value="gross">Gross</option>
                                <option value="net">Net</option>
                            </select>
                        </div>
                    </div>
                </control>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control label="Total Gross Earnings" before-after="pound-on-dark">
                    <input autocomplete="off" :value="totalGrossIncome" v-two-dp disabled type="number"/>
                </control>

                <control label="Total Net Earnings" before-after="pound-on-dark">
                    <input autocomplete="off" :value="totalNetIncome" v-two-dp disabled type="number"/>
                </control>
            </div>
        </div>

        <div slot="results-panel" v-show="totalNetIncome > 0">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto p-earnings__calc">
                <h2>
                    Explanation
                </h2>

                <p>
                    With annual gross earnings of {{ currencyFormat(totalGrossIncome || 0) }}, from {{ dateFrom }} to {{ dateTo }},
                    {{ currencyFormat(totalGrossIncome - totalNetIncome || 0) }} of tax and/or NI contributions would be paid annually.
                </p>

                <p>
                    This results in total Net earnings of {{ currencyFormat(totalNetIncome || 0)}}
                </p>

                <p class="hotfix-width">
                    The following chart provides a visual indication of income tax paid on employment
                    income, and any national insurance contributions applicable on employment income:
                </p>

                <div v-if="totalGrossIncome" class="chart">
                    <pie-chart :chart-data="chartData" :options="chartOptions"></pie-chart>
                </div>

                <h2>
                    Earnings
                </h2>

                <p>
                    Between {{ dateFrom }} and {{ dateTo }} the Net of earnings would be {{ currencyFormat(totalNetIncome * dateDiff())  }}
                </p>

                <!-- to be updated when copy received -->
                <!-- <h2>
                    Calculation
                </h2>

                <p>
                    [ Insert explanation here of how income tax was calculated — each income tax brackets allowance for the
                    fiscal year etc. ]
                </p> -->
            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPage';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import miniCalcIcons from 'components/Icons.vue';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import DateInput from 'components/form/control/DateInput.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import Moment from 'moment';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import PieChart from 'components/graphs/PieChart.js';

    export default {
        name: 'earnings',
        data: function () {
            return {
                chartData: {},
                chartOptions: {},
                showResults: false
            }
        },
        created() {
            this.resetForm();
        },
        computed: {
            ...mapGetters('mini/earnings', {
                findValue: 'findValue',
            }),
            dateOfBirth: {
                get() {
                    return this.findValue('dateOfBirth');
                },
                set(value) {
                    this.updateValue({key: 'dateOfBirth', value});
                }
            },
            gender: {
                get() {
                    return this.findValue('gender');
                },
                set(value) {
                    this.updateValue({key: 'gender', value});
                }
            },
            isRetired: {
                get() {
                    return this.findValue('isRetired');
                },
                set(value) {
                    this.updateValue({key: 'isRetired', value});
                }
            },
            retirementDate: {
                get() {
                    return this.findValue('retirementDate');
                },
                set(value) {
                    this.updateValue({key: 'retirementDate', value});
                }
            },
            dateFrom: {
                get() {
                    return this.findValue('dateFrom');
                },
                set(value) {
                    this.updateValue({key: 'dateFrom', value});
                }
            },
            dateTo: {
                get() {
                    return this.findValue('dateTo');
                },
                set(value) {
                    this.updateValue({key: 'dateTo', value});
                }
            },
            employmentType: {
                get() {
                    return this.findValue('employmentType');
                },
                set(value) {
                    this.updateValue({key: 'employmentType', value});
                }
            },
            employmentIncome: {
                get() {
                    return this.findValue('employmentIncome');
                },
                set(value) {
                    this.updateValue({key: 'employmentIncome', value});
                }
            },
            employmentIncomeNetGross: {
                get() {
                    return this.findValue('employmentIncomeNetGross');
                },
                set(value) {
                    this.updateValue({key: 'employmentIncomeNetGross', value});
                }
            },
            totalGrossIncome: {
                get() {
                    return this.findValue('totalGrossIncome');
                },
            },
            totalNetIncome: {
                get() {
                    return this.findValue('totalNetIncome');
                },
            },
        },
        mixins: [
            FormValidator,
            currencyFormat,
            CalculatorKeyBindings
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            Intro,
            PieChart
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        watch: {
            totalGrossIncome: function (value, newValue) {
                if (value !== newValue) {
                    this.updateChart();
                }
            }
        },
        methods: {
            ...mapActions('mini/earnings', {
                updateValue: 'updateValue',
                calculateEarnings: 'calculateEarnings',
                resetForm: 'reset',
                updateRetirement: 'updateRetirement'
            }),
            isValid() {
                if (this.isRetired) {
                    return this.isRetiredYesValid();
                }

                return this.isRetiredNoValid();
            },
            isRetiredYesValid() {
                return (
                    this.dateOfBirth !== null && Moment(this.dateOfBirth, 'DD/MM/YYYY').isValid()
                    && this.dateFrom !== null && Moment(this.dateFrom, 'DD/MM/YYYY').isValid()
                    && this.dateTo !== null && Moment(this.dateTo, 'DD/MM/YYYY').isValid()
                    && this.dateFromIsBeforeDateTo()
                    && this.employmentIncome > 0
                );
            },
            isRetiredNoValid() {
                return (
                    this.dateOfBirth !== null && Moment(this.dateOfBirth, 'DD/MM/YYYY').isValid()
                    && this.retirementDate !== null && Moment(this.retirementDate, 'DD/MM/YYYY').isValid()
                    && this.dateFrom !== null && Moment(this.dateFrom, 'DD/MM/YYYY').isValid()
                    && this.dateTo !== null && Moment(this.dateTo, 'DD/MM/YYYY').isValid()
                    && this.dateFromIsBeforeDateTo()
                    && this.employmentIncome > 0
                );
            },
            retirementDateIsValid() {
                if (this.retirementDate === null) {
                    return true;
                }

                let retirementDateMoment = Moment(this.retirementDate, 'DD/MM/YYYY');

                return retirementDateMoment.isValid();
            },
            dateFromIsValid() {
                if (this.dateFrom === null) {
                    return true;
                }

                let dateFromMoment = Moment(this.dateFrom, 'DD/MM/YYYY');

                return (
                    dateFromMoment.isValid()
                );
            },
            dateToIsValid() {
                if (this.dateTo === null) {
                    return true;
                }

                let dateToMoment = Moment(this.dateTo, 'DD/MM/YYYY');

                return (
                    dateToMoment.isValid()
                    && this.dateFromIsBeforeDateTo()
                );
            },
            dateFromIsBeforeDateTo() {
                let dateFromMoment = Moment(this.dateFrom, 'DD/MM/YYYY');
                let dateToMoment = Moment(this.dateTo, 'DD/MM/YYYY');
                return (
                    dateFromMoment.isValid()
                    && dateToMoment.isValid()
                    && dateFromMoment.isBefore(dateToMoment)
                );
            },
            reCalculateRetired() {
                // if (this.dateOfBirth === null || this.dateFrom === null) {
                //     this.isRetired = false;
                //     return;
                // }
                //
                // let dateOfBirthM = Moment(this.dateOfBirth, 'DD/MM/YYYY');
                // let dateFromM = Moment(this.dateFrom, 'DD/MM/YYYY');
                //
                // if (!dateOfBirthM.isValid() || !dateFromM.isValid()) {
                //     this.isRetired = false;
                //     return;
                // }
                //
                // let retirementDateM = dateOfBirthM.add(65, 'years');
                //
                // this.retirementDate = retirementDateM.format('DD/MM/YYYY');
                //
                // this.isRetired = retirementDateM.isBefore(dateFromM);

                this.updateRetirement().then(
                    this.reCalculateEarnings()
                );
            },
            reCalculateEarnings() {
                if (!this.isValid()) {
                    return;
                }

                this.calculateEarnings();
                this.hidePrompt();
                this.showResults = true;
            },
            twoDp(value) {
                return (Math.round(value * 100) / 100).toFixed(2);
            },
            updateChart() {
                let netIncome = this.twoDp(this.totalNetIncome);
                let taxPaid = this.twoDp(this.totalGrossIncome - this.totalNetIncome);

                this.chartData = {
                    labels: ['Net Earnings', 'Income Tax & NI Contributions Paid'],
                    datasets: [
                        {
                            label: 'Gross Income Breakdown',
                            backgroundColor: [
                                '#4A89E5',
                                '#c5ecfe',
                            ],
                            data: [netIncome, taxPaid],
                        }
                    ]
                };

                this.chartOptions = {
                    tooltips: {
                        callbacks: {
                            label: function(tooltipItem, data) {
                                let allData = data.datasets[tooltipItem.datasetIndex].data;
                                let tooltipLabel = data.labels[tooltipItem.index];
                                let tooltipData = allData[tooltipItem.index];
                                return tooltipLabel + ': £' + tooltipData;
                            }
                        }
                    }
                };
            },
            dateDiff() {
                let dateFromM = Moment(this.dateFrom, 'DD/MM/YYYY');
                let dateToM = Moment(this.dateTo, 'DD/MM/YYYY');

                return dateToM.diff(dateFromM, 'y', true);
            }
        }
    }
</script>



// WEBPACK FOOTER //
// earnings.vue?2cd69d32